import { useState } from "react";
import { Wind, Flame, Droplets, ShieldCheck, FileText, Layers, Plus, Minus } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const ICONS = {
  ventilasjon: Wind,
  varme: Flame,
  sanitar: Droplets,
  brann: ShieldCheck,
  arbeidstegninger: FileText,
  bim: Layers,
};

const Services = () => {
  const [openId, setOpenId] = useState("ventilasjon");
  const { t } = useLanguage();
  const items = t("services.items");

  return (
    <section id="tjenester" className="scroll-mt-20 border-t border-slate-200 bg-white py-20 sm:py-28" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-sky-700" data-testid="services-badge">
            {t("services.badge")}
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl" data-testid="services-title">
            {t("services.title")}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
            {t("services.intro")}
          </p>
        </Reveal>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s, idx) => {
            const Icon = ICONS[s.id] || Layers;
            const open = openId === s.id;
            return (
              <Reveal key={s.id} delay={idx * 0.07} className="h-full">
                <div
                  className={`group h-full rounded-lg border bg-white p-7 transition-all duration-300 ${
                    open ? "border-sky-600/50 shadow-md" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                  }`}
                  data-testid={`service-card-${s.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="rounded-md border border-sky-100 bg-sky-50 p-3 text-sky-700">
                      <Icon size={22} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{s.code}</span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.short}</p>

                  {open && (
                    <ul className="mt-5 space-y-2.5 border-t border-slate-100 pt-5" data-testid={`service-details-${s.id}`}>
                      {s.details.map((d) => (
                        <li key={d} className="flex items-start gap-2.5 text-sm text-slate-700">
                          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-sky-600" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    onClick={() => setOpenId(open ? null : s.id)}
                    data-testid={`service-toggle-${s.id}`}
                    className="mt-5 inline-flex items-center gap-2 font-mono text-xs font-medium uppercase tracking-widest text-sky-700 transition-colors hover:text-sky-900"
                  >
                    {open ? <Minus size={14} /> : <Plus size={14} />}
                    {open ? t("services.hideDetails") : t("services.showDetails")}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
